import { Column, ColumnSizingState } from '@tanstack/react-table';
import { CloseIcon, ColumnVisibilityIcon, ResizeColumnsIcon, VerticalThreeDotIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import classNames from 'classnames';
import { useState } from 'react';

type ColumnOptionsContentProps = {
  columnTitle: string;
  onClose: () => void;
  onResetWidth: () => void;
  onHide: () => void;
  hasCustomWidth: boolean;
  canHide: boolean;
};

const ColumnOptionsContent = ({
  columnTitle,
  onClose,
  onResetWidth,
  onHide,
  hasCustomWidth,
  canHide,
}: ColumnOptionsContentProps) => {
  const translate = useTranslate();
  const optionClass =
    'flex items-center gap-3 px-3 py-2 rounded-12 text-xs font-medium text-mine-shaft hover:bg-gray-concrete w-full text-left';
  return (
    <div>
      <div className='flex items-center justify-between pl-4 pr-2 py-1 border-b border-gray-concrete'>
        <span className='text-xs font-semibold text-mine-shaft'>
          {translate('all.table.columnOptions.title', { columnTitle })}
        </span>
        <Button size='small' color='quaternary' icon={() => <CloseIcon color='#3C3C3C' />} onClick={onClose} />
      </div>
      <div className='flex flex-col p-1.5 gap-0.5'>
        {hasCustomWidth && (
          <button className={optionClass} onClick={onResetWidth}>
            <ResizeColumnsIcon className='w-4 h-4 shrink-0' color='currentColor' />
            {translate('all.table.columnOptions.resetWidth')}
          </button>
        )}
        {canHide && (
          <TooltipWrapper content={translate('all.table.columnOptions.hideTooltip')} placement='right'>
            <button className={classNames(optionClass, 'text-red-negative')} onClick={onHide}>
              <ColumnVisibilityIcon className='w-4 h-4 shrink-0' color='#ED290E' />
              {translate('all.table.columnOptions.hide')}
            </button>
          </TooltipWrapper>
        )}
      </div>
    </div>
  );
};

export type ColumnOptionsButtonProps = {
  column: Column<any, unknown>;
  storedColumnSizing: ColumnSizingState;
  canHide: boolean;
  onResetWidth: () => void;
};

export const ColumnOptionsButton = ({
  column,
  storedColumnSizing,
  canHide,
  onResetWidth,
}: ColumnOptionsButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const columnTitle = column.getLabel?.() ?? column.id;

  return (
    <TooltipWrapper
      interactive
      useDefaultStyle={false}
      trigger='click'
      arrow={false}
      placement='bottom-end'
      open={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
      tooltipClassNames='bg-white rounded-18 shadow-overlay min-w-55 overflow-hidden'
      content={
        <ColumnOptionsContent
          columnTitle={columnTitle}
          onClose={() => setIsOpen(false)}
          onResetWidth={() => {
            setIsOpen(false);
            onResetWidth();
          }}
          onHide={() => {
            setIsOpen(false);
            column.toggleVisibility(false);
          }}
          hasCustomWidth={column.getCanResize() && storedColumnSizing[column.id] !== undefined}
          canHide={canHide}
        />
      }
    >
      <button
        className={classNames(
          'relative shrink-0 self-stretch w-8 flex items-center justify-center hover:bg-gray-concrete transition-opacity z-2 mr-4',
          'opacity-0 group-hover/header:opacity-100',
          { 'opacity-100': isOpen },
        )}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <VerticalThreeDotIcon color='#969696' className='h-4' />
      </button>
    </TooltipWrapper>
  );
};
